import { CurrentUser, QStack } from '@qualio/ui-components';
import { useTranslation } from 'react-i18next';
import {
  DocumentStatus,
  QualioDocument,
} from '../../../../../api/model/document';
import { WorkspaceTabs } from '../WorkspaceV2';
import { AssignTraineesButtonV2 } from './BulkActionButtons/AssignTrainees/AssignTraineesButtonV2';
import { BulkExportButton } from './BulkActionButtons/BulkExportButton/BulkExportButton';
import { ChangeOwnerButton } from './BulkActionButtons/ChangeOwnerButton/ChangeOwnerButton';
import { DeleteButton } from './BulkActionButtons/ChangeStatusButtons/DeleteButton/DeleteButton';
import { MakeEffectiveButton } from './BulkActionButtons/ChangeStatusButtons/MakeEffectiveButton/MakeEffectiveButton';
import { RetireButton } from './BulkActionButtons/ChangeStatusButtons/RetireButton';
import { RevertToDraftButton } from './BulkActionButtons/ChangeStatusButtons/RevertToDraftButton/RevertToDraftButton';
import { SendForApprovalButton } from './BulkActionButtons/ChangeStatusButtons/SendForApprovalButton/SendForApprovalButton';
import { SendForReviewButton } from './BulkActionButtons/ChangeStatusButtons/SendForReviewButton/SendForReviewButton';
import { CreateNewDraftButton } from './BulkActionButtons/CreateNewDraftButton/CreateNewDraftButton';
import { ManageTagsButton } from './BulkActionButtons/ManageTagsButton/ManageTagsButton';

interface BulkActionsProps {
  currentTab: WorkspaceTabs;
  selectedDocuments: QualioDocument[];
  statuses: string[];
}

// Utility functions that were removed and are needed by other components
export const hasRequiredPermissionsForAction = (
  requiredPermissions: readonly string[],
  userPermissions: CurrentUser['permissions'],
) =>
  requiredPermissions.some((permission) =>
    Object.keys(userPermissions).includes(permission),
  );

export const hasAllowedStatusesForAction = (
  allowedStatuses: DocumentStatus[],
  selectedStatuses: string[],
) =>
  selectedStatuses.length
    ? selectedStatuses.every((status) =>
        allowedStatuses.map((s) => s.valueOf()).includes(status),
      )
    : false;

export const isValidTabForAction = (
  allowedTabs: WorkspaceTabs[],
  activeTab: WorkspaceTabs,
) => allowedTabs.map((t) => t.valueOf()).includes(activeTab);

export const BulkActions = (props: BulkActionsProps) => {
  const { t } = useTranslation();
  const selectedDocuments = props.selectedDocuments;
  const statuses = props.statuses;

  const getCommonProps = () => ({
    currentTab: props.currentTab,
    statuses,
    selectedDocuments,
  });

  return (
    <QStack id="bulk-actions" isInline>
      <SendForReviewButton
        label={t('buttons.sendForReview')}
        requiredPermissions={['can_author_doc']}
        {...getCommonProps()}
      />
      <SendForApprovalButton
        label={t('buttons.sendForApproval')}
        requiredPermissions={['can_author_doc']}
        {...getCommonProps()}
      />
      <DeleteButton
        label={t('buttons.delete')}
        requiredPermissions={['can_change_doc_owner']}
        {...getCommonProps()}
      />
      <RevertToDraftButton
        label={t('buttons.revertToDraft')}
        requiredPermissions={['can_author_doc']}
        {...getCommonProps()}
      />
      <CreateNewDraftButton
        label={t('buttons.createNewDraft')}
        requiredPermissions={['can_author_doc']}
        {...getCommonProps()}
      />
      <MakeEffectiveButton
        label={t('buttons.makeEffective')}
        requiredPermissions={['can_make_doc_effective']}
        {...getCommonProps()}
      />
      <RetireButton
        label={t('buttons.retire')}
        requiredPermissions={['can_retire_doc']}
        {...getCommonProps()}
      />
      <ChangeOwnerButton
        label={t('buttons.changeOwner')}
        requiredPermissions={['can_change_doc_owner']}
        {...getCommonProps()}
      />
      <AssignTraineesButtonV2
        requiredPermissions={['can_author_doc']}
        {...getCommonProps()}
      />
      <ManageTagsButton
        requiredPermissions={['can_author_doc']}
        {...getCommonProps()}
      />
      <BulkExportButton {...getCommonProps()} />
    </QStack>
  );
};
